import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  CustomTheme,
  Fab,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery } from '@apollo/client';
import { useActionPopoverContext, UserContext } from 'op-contexts';
import { GET_PRACTITIONER } from 'op-graphql/queries';
import {
  GET_NOTE_TYPES,
  PatientNote,
  GET_PATIENT_NOTES,
  NoteType,
  usePatientNotes,
} from 'op-components/RO/Notes/Notes';
import {
  ADD_PATIENT_NOTE,
  UPDATE_PATIENT_NOTE,
  GET_PATIENT_PROFILE,
} from 'op-pages/RO/PatientSummary/PatientProfileQueries';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { PAT_ID_1_REFRESH_TIME, Region } from 'shared-components/enums';
import { region } from 'op-components';
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar';
import useInterval from 'shared-components/utils/useInterval';
import FloatingPopoverContainer from './FloatingPopoverContainer';

interface Props {
  patientId: string;
  positionKey?: string;
  soloFab?: boolean;
}

interface NotesPopoverTooltipProps {
  title: string;
  popperContainerEl?: HTMLElement | null;
  disableHoverListener?: boolean;
  children: JSX.Element;
}

const NotesPopoverTooltip = ({
  title,
  popperContainerEl,
  disableHoverListener,
  children,
}: NotesPopoverTooltipProps): JSX.Element => (
  <Tooltip
    title={title}
    disableHoverListener={disableHoverListener}
    PopperProps={{
      disablePortal: Boolean(popperContainerEl),
      container: popperContainerEl ?? undefined,
    }}>
    {children}
  </Tooltip>
);

const NoteTypeButtons = (props: {
  noteTypes: NoteType[];
  selected: string;
  setSelectedNoteType: (name: string) => void;
}): JSX.Element => {
  const theme = useTheme<CustomTheme>();
  const { noteTypes, selected, setSelectedNoteType } = props;

  if (!noteTypes?.length) return <div />;

  return (
    <ButtonGroup id="notesNoteTypeButtons" aria-label="Note type" sx={{ width: 'fit-content', flexWrap: 'wrap' }}>
      {[...noteTypes]
        .sort((a: any, b: any) => a.listWeight - b.listWeight)
        .map((note: NoteType, index: number) => {
          const activeButton = selected === note.name;
          return (
            <Button
              key={note.id || index}
              id={`note-type-button-${note.id ?? index}`}
              data-testid={`notes-noteType-${index}`}
              onClick={() => {
                setSelectedNoteType(note.name);
              }}
              variant="text"
              sx={{
                textTransform: 'uppercase',
                color: 'darkslategrey',
                fontSize: '1rem',
                borderBottom: activeButton ? `4px solid ${theme.palette.primary.main}` : 'none',
                paddingBottom: activeButton ? '8px !important' : '12px !important',
                borderRadius: '0px',
              }}>
              <span>{note.alias ? note.alias : note.name}</span>
            </Button>
          );
        })}
    </ButtonGroup>
  );
};

const NotesPopover = ({ positionKey, patientId, soloFab = false }: Props): JSX.Element => {
  const theme = useTheme<CustomTheme>();
  const { state } = useContext(UserContext);
  const {
    anchorEl,
    isNotesPopoverOpen,
    openFloatingPanel,
    closeFloatingPanel,
    isFocusedModalOpen,
    popoverContainerEl,
    setPopoverContainerEl,
  } = useActionPopoverContext();

  const [selectedNoteType, setSelectedNoteType] = useState<string>('');
  const [draftNote, setDraftNote] = useState<PatientNote | null>(null);
  const [noteMessage, setNoteMessage] = useState<string>('');
  const [noteTypes, setNoteTypes] = useState<NoteType[]>([]);
  const [isNotesScrolled, setIsNotesScrolled] = useState(false);
  const [snackbarState, setSnackbarState] = useState<{
    open: boolean;
    message: string;
    type: 'notification';
  }>({
    open: false,
    message: '',
    type: 'notification',
  });
  const lastSavedDraftMessageRef = useRef('');
  const lastSavedDraftNoteTypeRef = useRef('');

  const { data: pracData } = useQuery(GET_PRACTITIONER, {
    variables: { patientId },
    skip: !patientId,
  });

  const { data: noteTypesQuery, loading: loadingNoteTypes } = useQuery(GET_NOTE_TYPES, {
    variables: { patientId },
    fetchPolicy: 'cache-and-network',
  });

  const { data: patientProfileData, refetch: refetchPatientProfile } = useQuery(GET_PATIENT_PROFILE, {
    variables: { patientId },
  });

  const { notes } = usePatientNotes(patientId);

  useInterval(
    () => {
      refetchPatientProfile();
    },
    patientProfileData?.patient?.userProfile?.hasPatId1 ? null : PAT_ID_1_REFRESH_TIME,
  );

  useEffect(() => {
    const noteTypesData = noteTypesQuery?.notesTypes;
    if (noteTypesData?.length) {
      setSelectedNoteType((prev) => prev || noteTypesData[0].name);
      setNoteTypes(noteTypesData);
    }
  }, [noteTypesQuery]);

  useEffect(() => {
    if (notes) {
      const filteredDraft = notes.filter(
        (note: PatientNote) => note?.isDraft && note?.createdBy?.id === pracData?.practitioner?.id,
      )[0];
      setDraftNote(filteredDraft || null);
      const draftMessage = filteredDraft?.formattedMessage || '';
      const draftNoteType = filteredDraft?.messageType || '';
      lastSavedDraftMessageRef.current = draftMessage;
      lastSavedDraftNoteTypeRef.current = draftNoteType;
      setSelectedNoteType((prev) => filteredDraft?.messageType || prev);
      setNoteMessage(draftMessage);
    }
  }, [notes, pracData?.practitioner?.id]);

  const [updateNoteMutation] = useMutation(UPDATE_PATIENT_NOTE, {
    refetchQueries: [{ query: GET_PATIENT_NOTES, variables: { patientId } }],
  });

  const [addNote, { loading: isSubmittingNote }] = useMutation(ADD_PATIENT_NOTE, {
    refetchQueries: [{ query: GET_PATIENT_NOTES, variables: { patientId } }],
    onCompleted: (): void => {
      lastSavedDraftMessageRef.current = '';
      lastSavedDraftNoteTypeRef.current = '';
      setNoteMessage('');
      setDraftNote(null);
      closeFloatingPanel();
      if (state?.primaryRole === 'ro') {
        setSnackbarState({
          open: true,
          message: 'New note added',
          type: 'notification',
        });
      }
    },
  });

  const submitNote = (message: string, noteType: string) => {
    if (message && noteType) {
      addNote({ variables: { patientId, messageType: noteType, message, noteId: draftNote?.id } });
    }
  };

  const persistDraftNote = useCallback(
    (message: string) => {
      if (!selectedNoteType) return;
      if (!message.trim() && !draftNote?.id) return;
      if (message === lastSavedDraftMessageRef.current && selectedNoteType === lastSavedDraftNoteTypeRef.current) {
        return;
      }
      updateNoteMutation({
        variables: { patientId, message, messageType: selectedNoteType, noteId: draftNote?.id },
      });
      lastSavedDraftMessageRef.current = message;
      lastSavedDraftNoteTypeRef.current = selectedNoteType;
    },
    [draftNote?.id, patientId, selectedNoteType, updateNoteMutation],
  );

  const handleNoteScroll = useCallback((e: React.UIEvent<HTMLTextAreaElement>) => {
    setIsNotesScrolled(e.currentTarget.scrollTop > 0);
  }, []);

  const hasPatId1 = Boolean(patientProfileData?.patient?.userProfile?.hasPatId1);
  const disableNotes = !state.hasStaffId || (!hasPatId1 && region !== Region.UK) || noteTypes.length === 0;

  const handlePopoverPaperRef = useCallback(
    (node: HTMLDivElement | null) => {
      setPopoverContainerEl((prev) => (prev === node ? prev : node));
    },
    [setPopoverContainerEl],
  );

  const handleOpen = useCallback(() => {
    openFloatingPanel('notes');
  }, [openFloatingPanel]);

  const handleClose = useCallback(() => {
    closeFloatingPanel();
  }, [closeFloatingPanel]);

  const resetLocalOnPositionChange = useCallback(() => {
    closeFloatingPanel();
  }, [closeFloatingPanel]);

  const hasUnsubmitted = Boolean(noteMessage.trim());

  const fabTooltipTitle = hasUnsubmitted ? 'Unsubmitted note' : 'Add Notes';

  useEffect(() => {
    if (!noteMessage) {
      setIsNotesScrolled(false);
    }
  }, [noteMessage]);

  const unsubmittedDotSx = {
    position: 'absolute',
    backgroundColor: theme.palette.error.main,
    zIndex: theme.zIndex.fab + 1,
    width: '10px',
    height: '10px',
    ...(soloFab
      ? {
          top: 12,
          left: 12,
        }
      : {
          top: 8,
          left: 8,
        }),
    borderRadius: '50%',
    padding: 0,
    boxSizing: 'border-box',
    aspectRatio: '1',
  };

  const fabWrapperSx = {
    display: 'inline-flex',
    position: 'relative',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px) scale(1.1)',
    },
  };

  const fabSx = {
    borderRadius: soloFab ? '50%' : '10px',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: soloFab ? theme.palette.primary.dark : theme.palette.grey[300],
    ...(!soloFab && {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.main,
    }),
    boxShadow: soloFab ? 1 : 0,
    transition: 'box-shadow 0.2s ease-in-out, filter 0.2s ease-in-out',
    '&:hover': {
      filter: 'brightness(1) saturate(1.3)',
      boxShadow: soloFab ? 8 : 0,
    },
  };

  const fabSection = (
    <Box
      sx={{
        zIndex: isFocusedModalOpen ? theme.zIndex.modal - 2 : theme.zIndex.fab,
      }}>
      <Tooltip title={fabTooltipTitle} placement="left">
        <Box sx={fabWrapperSx}>
          {hasUnsubmitted && !isNotesPopoverOpen && <Box aria-hidden sx={unsubmittedDotSx} />}
          <Fab
            color={soloFab ? 'primary' : undefined}
            data-testid="notes-fab"
            tabIndex={0}
            onClick={handleOpen}
            sx={fabSx}>
            <EditIcon sx={soloFab ? { color: theme.palette.common.white } : { color: theme.palette.primary.main }} />
            <Box component="span" sx={visuallyHidden}>
              {fabTooltipTitle}
            </Box>
          </Fab>
        </Box>
      </Tooltip>
    </Box>
  );

  const headerSection = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
      <NotesPopoverTooltip
        title="Unsubmitted note"
        popperContainerEl={popoverContainerEl}
        disableHoverListener={!hasUnsubmitted}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Badge
            variant="dot"
            invisible={!hasUnsubmitted}
            overlap="rectangular"
            sx={{
              overflow: 'visible',
              '& .MuiBadge-badge': {
                backgroundColor: theme.palette.error.main,
                right: -6,
                top: 8,
                borderRadius: '50%',
              },
            }}
            {...(hasUnsubmitted ? { 'data-test-id': 'banner-unsubmitted-notes' } : {})}>
            <Typography variant="h6">Notes</Typography>
          </Badge>
        </Box>
      </NotesPopoverTooltip>
      <IconButton
        id="notes-close-button"
        aria-label="Close Notes"
        data-testid="notes-close"
        onClick={handleClose}
        size="small">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <>
      <FloatingPopoverContainer
        fab={fabSection}
        isOpen={isNotesPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        isFocusedModalOpen={isFocusedModalOpen}
        positionKey={positionKey}
        onPositionKeyChange={resetLocalOnPositionChange}
        onPopoverPaperRef={handlePopoverPaperRef}
        header={headerSection}
        isHeaderScrolled={isNotesScrolled}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            px: 2,
            pt: 1,
            pb: 2,
          }}>
          {!disableNotes && (
            <Stack sx={{ mb: 1, flexShrink: 0 }}>
              <NoteTypeButtons
                noteTypes={noteTypes}
                selected={selectedNoteType}
                setSelectedNoteType={setSelectedNoteType}
              />
            </Stack>
          )}
          <TextField
            id="notes-textarea"
            multiline
            fullWidth
            disabled={disableNotes}
            placeholder={disableNotes ? 'Please add notes in your EMR.' : 'Type your notes here...'}
            value={noteMessage}
            onChange={(e) => setNoteMessage(e.target.value)}
            onBlur={(e) => persistDraftNote(e.target.value)}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            inputProps={{ onScroll: handleNoteScroll }}
            sx={{
              flex: 1,
              minHeight: 0,
              '& .MuiInputBase-root': {
                height: '100%',
                minHeight: 0,
                alignItems: 'stretch',
              },
              '& .MuiInputBase-inputMultiline': {
                height: '100% !important',
                overflowY: 'auto !important',
              },
            }}
          />
          <Button
            id="notes-add-button"
            data-testid="notes-add-button"
            variant="contained"
            fullWidth
            sx={{ mt: 2, flexShrink: 0 }}
            startIcon={isSubmittingNote ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            disabled={!noteMessage.trim() || disableNotes || loadingNoteTypes || !selectedNoteType || isSubmittingNote}
            onMouseDown={(e) => {
              e.preventDefault();
              if (isSubmittingNote) return;
              submitNote(noteMessage, selectedNoteType);
            }}>
            {isSubmittingNote ? 'ADDING NOTE' : 'ADD NOTE'}
          </Button>
        </Box>
      </FloatingPopoverContainer>
      <NotificationSnackbar
        open={snackbarState.open}
        onClose={() =>
          setSnackbarState((prev) => ({
            ...prev,
            open: false,
          }))
        }
        message={snackbarState.message}
        autoHideDuration={5000}
        type={snackbarState.type}
      />
    </>
  );
};

export default NotesPopover;
